import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import QueryString from "qs";

import { useGetCustomisation, useGetProduct } from "queries/product-queries";
import {
  MISSING_CUSTOMISATION_ERROR,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";

type PdfResponse = { components: { component: string; url: string }[] };

export const adminKeys = {
  all: [{ scope: "admin" }] as const,
  pdfs: (slug: string, customisation: QueryString.ParsedQs) =>
    [
      {
        ...adminKeys.all[0],
        slug,
        customisation,
        data: "pdfs",
      },
    ] as const,
  pdf: (slug: string, customisation: QueryString.ParsedQs, component: string) =>
    [
      {
        ...adminKeys.pdfs(slug, customisation)[0],
        component,
      },
    ] as const,
  displayMode: () =>
    [
      {
        ...adminKeys.all[0],
        data: "canvasDisplayMode",
      },
    ] as const,
};

/**
 * Set defaults for admin queries
 *
 * Setting these here applies them globally whether called directly or via custom hooks
 *
 * Also helpful for testing as they can be easily overriden
 * (e.g. to stop retries from slowing tests down)
 */
export function setAdminQueryDefaults(queryClient: QueryClient) {
  queryClient.setQueryDefaults([{ data: "canvasDisplayMode" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  queryClient.setQueryDefaults([{ data: "pdfs" }], {
    staleTime: Infinity,
    cacheTime: Infinity,
  });
}

/**
 * Request PDFs for this product.
 *
 * Returns an array of `components` with URLs that resolve later.
 */
export function useGeneratePdfs() {
  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (!customisation) {
    throw MISSING_CUSTOMISATION_ERROR;
  }

  return useMutation<PdfResponse>({
    mutationFn: async () => {
      const response = await axios.post(
        `/_canvas/api/muse/${
          customisation.muse_slug_override ?? product.museSlug
        }/pdf`,
        {
          barcode: "",
          cover: "hardback",
          ...customisation,
        }
      );

      return response.data;
    },
    cacheTime: Infinity,
  });
}

/**
 * Attempt to get a PDF that has been generated.
 *
 * Poll the `404`s until it's ready.
 */
export function useGetPdfComponent(component: string, url: string) {
  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (!customisation) {
    throw MISSING_CUSTOMISATION_ERROR;
  }

  return useQuery<string, AxiosError>({
    queryKey: adminKeys.pdf(product.museSlug, customisation, component),
    queryFn: async () => {
      await axios.get(url, { timeout: Infinity });
      return url;
    },
    retry: (failureCount, error) => {
      if (error.response?.status === 404) {
        return true;
      }
      // Only retry three times if the error is unexpected
      return failureCount < 3;
    },
    // Will retry every 2 seconds
    retryDelay: 2000,
  });
}

type DisplayMode = "default" | "diagnostic";

export function useGetDisplayMode() {
  return useQuery({
    queryKey: adminKeys.displayMode(),
    queryFn: async () => {
      return (localStorage.getItem("canvasDisplayMode") ??
        "default") as DisplayMode;
    },
  });
}

export function useSetDisplayMode() {
  const queryClient = useQueryClient();

  return useMutation<any, any, { mode: DisplayMode }>({
    mutationFn: async ({ mode }) => {
      localStorage.setItem("canvasDisplayMode", mode);
      return true;
    },
    onSettled: () => {
      queryClient.invalidateQueries(adminKeys.displayMode());
    },
  });
}
